import React, { useCallback, useEffect, useRef, useState } from "react"
import ReactCrop, {
  centerCrop,
  Crop,
  makeAspectCrop,
  PixelCrop,
} from "react-image-crop"
import "react-image-crop/dist/ReactCrop.css"
import { Client } from "@xmpp/client"
import XmppVCardApi from "../api/xmppVCardApi"
import FileUploadApi from "../api/fileUploadApi"
import { syncSelfVcard } from "../stream/keepConnected"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
// Using custom range input with Tailwind styling
import { Label } from "@/components/ui/label"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import i18n from "@/lib/i18n/i18n"

type ProfilePictureUploadModalProps = {
  imgSrc: string
  isOpen: boolean
  onClose: () => void
  client: Client
  myJid: string
}

const MIN_ZOOM = 1
const MAX_ZOOM = 3
const ASPECT_RATIO = 1
const CROP_SIZE = 300 // Standard size for profile pictures (adjust as needed)

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  const minDimension = Math.min(mediaWidth, mediaHeight)
  const widthPercentage = (minDimension / mediaWidth) * 75

  const crop = centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: widthPercentage,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  )
  crop.y = Math.min(65 - crop.height, Math.max(0, 50 - crop.height / 2))

  return crop
}

const ProfilePictureUploadModal = ({
  imgSrc,
  isOpen,
  onClose,
  client,
}: ProfilePictureUploadModalProps) => {
  const { t } = useTranslation()
  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
  const [zoom, setZoom] = useState(1)
  const imgRef = useRef<HTMLImageElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setImageLoaded(false)
    } else {
      setCrop(undefined)
      setCompletedCrop(undefined)
      setZoom(1)
    }
  }, [isOpen])

  // Set initial crop when image loads
  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget

    // Initialize with a centered crop with proper aspect ratio
    const newCrop = centerAspectCrop(width, height, ASPECT_RATIO)
    setCrop(newCrop)

    // Calculate the initial completedCrop in pixels
    const pixelCrop: PixelCrop = {
      x: Math.round(width * (newCrop.x / 100)),
      y: Math.round(height * (newCrop.y / 100)),
      width: Math.round(width * (newCrop.width / 100)),
      height: Math.round(height * (newCrop.height / 100)),
      unit: "px",
    }

    setCompletedCrop(pixelCrop)
    setImageLoaded(true)
  }

  // Ensure completedCrop is set even without user interaction
  useEffect(() => {
    if (imageLoaded && crop && imgRef.current) {
      const { width, height } = imgRef.current

      const pixelCrop: PixelCrop = {
        x: Math.round(width * (crop.x / 100)),
        y: Math.round(height * (crop.y / 100)),
        width: Math.round(width * (crop.width / 100)),
        height: Math.round(height * (crop.height / 100)),
        unit: "px",
      }

      setCompletedCrop(pixelCrop)
    }
  }, [imageLoaded, crop])

  const getCroppedImg = useCallback(
    async (image: HTMLImageElement, pixelCrop: PixelCrop): Promise<Blob> => {
      const canvas = document.createElement("canvas")
      const ctx = canvas.getContext("2d")

      if (!ctx) {
        throw new Error("No 2d context")
      }

      // Set canvas to our desired output size
      canvas.width = CROP_SIZE
      canvas.height = CROP_SIZE

      // The displayed image dimensions (what the user sees)
      const {
        width: displayedWidth,
        height: displayedHeight,
        naturalWidth,
        naturalHeight,
      } = image

      // Calculate the center of the crop in the displayed image
      const cropCenterX = pixelCrop.x + pixelCrop.width / 2
      const cropCenterY = pixelCrop.y + pixelCrop.height / 2

      // Convert to percentages of the displayed image
      const centerXPercent = cropCenterX / displayedWidth
      const centerYPercent = cropCenterY / displayedHeight

      // Calculate the offsets from center in the original image coordinates
      const offsetXPercent = (centerXPercent - 0.5) / zoom
      const offsetYPercent = (centerYPercent - 0.5) / zoom

      // Find the center of our crop in the natural image coordinates
      const naturalCenterX = naturalWidth * (0.5 + offsetXPercent)
      const naturalCenterY = naturalHeight * (0.5 + offsetYPercent)

      // Calculate natural crop size with aspect ratio maintained
      const naturalCropSize = Math.min(
        naturalWidth,
        naturalHeight,
        ((pixelCrop.width / displayedWidth) * naturalWidth) / zoom,
        ((pixelCrop.height / displayedHeight) * naturalHeight) / zoom,
      )

      // Calculate the source coordinates for cropping
      const sourceX = Math.max(0, naturalCenterX - naturalCropSize / 2)
      const sourceY = Math.max(0, naturalCenterY - naturalCropSize / 2)

      // Draw the crop to the canvas
      ctx.drawImage(
        image,
        sourceX, // Source X in the original image
        sourceY, // Source Y in the original image
        naturalCropSize, // Width to take from the original image
        naturalCropSize, // Height to take from the original image
        0, // Destination X on canvas
        0, // Destination Y on canvas
        CROP_SIZE, // Destination width on canvas
        CROP_SIZE, // Destination height on canvas
      )

      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error("Canvas is empty"))
            }
          },
          "image/jpeg",
          1, // Maximum quality
        )
      })
    },
    [zoom], // Include zoom as a dependency
  )

  const handleSave = useCallback(async () => {
    try {
      if (!imgRef.current || !completedCrop) return

      setIsLoading(true)

      // Get cropped image blob
      const croppedImageBlob = await getCroppedImg(
        imgRef.current,
        completedCrop,
      )
      // Convert blob to File object with appropriate name and type
      const file = new File([croppedImageBlob], "profile-picture.jpg", {
        type: "image/jpeg",
      })
      try {
        // Upload using the file API
        const urls = await FileUploadApi.requestSlot(
          client,
          file.name,
          file.size.toString(),
          file.type,
        )
        const [get, put] = urls
        await fetch(put, {
          method: "PUT",
          body: file,
        })

        await XmppVCardApi.updateVCardPhoto(client, {
          extval: get,
        })
        await syncSelfVcard(client)
        // Close the dialog and show success message
        onClose()
        setIsLoading(false)
        window.appToaster.show({
          message: i18n.t("app.profilePicture.updated"),
          intent: "success",
        })
      } catch (e) {
        console.error(e)
        window.appToaster.show({
          message: i18n.t("app.profilePicture.updateFailed"),
          intent: "danger",
        })
      }
    } catch (error) {
      console.error("Error creating cropped image:", error)
      setIsLoading(false)
    }
  }, [client, completedCrop, getCroppedImg, onClose])

  return (
    <Dialog open={isOpen} onOpenChange={onClose} modal>
      <DialogContent
        className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-lg"
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          document.body.style.pointerEvents = ""
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t("app.profilePicture.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 space-y-6 overflow-hidden">
          <div className="relative max-h-[50vh] w-full overflow-scroll rounded-lg border">
            <ReactCrop
              className="left-1/2 -translate-x-1/2"
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={ASPECT_RATIO}
              circularCrop
            >
              <img
                ref={imgRef}
                src={imgSrc}
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: "center",
                }}
                className="transition-transform duration-200"
                onLoad={onImageLoad}
                alt={t("app.profilePicture.previewAlt")}
              />
            </ReactCrop>
          </div>

          <div className="space-y-3">
            <Label htmlFor="zoom-slider" className="text-sm font-medium">
              {t("app.profilePicture.zoom", { value: zoom.toFixed(1) })}
            </Label>
            <input
              id="zoom-slider"
              type="range"
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="slider h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600"
            />
          </div>
        </div>

        <DialogFooter className="mt-4 flex flex-shrink-0 justify-between gap-2 sm:justify-end">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!completedCrop || isLoading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("app.profilePicture.saving")}
              </>
            ) : (
              t("common.save")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ProfilePictureUploadModal
